<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getLegacyQueueRescueLevel,
  isAwaitingAgentReply,
} from 'shared/helpers/legacyQueue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const rescueLevel = computed(() =>
  getLegacyQueueRescueLevel(props.conversation)
);
const followUp = computed(
  () => props.conversation.additional_attributes?.aipayok_follow_up || {}
);
const isFollowUpActive = computed(() =>
  ['scheduled', 'due'].includes(followUp.value.state)
);
const isAwaitingReply = computed(
  () => rescueLevel.value === 0 && isAwaitingAgentReply(props.conversation)
);

const label = computed(() => {
  if (rescueLevel.value === 2) {
    return t('CONVERSATION.LEGACY_QUEUE.RESCUE_LEVEL_2');
  }
  if (rescueLevel.value === 1) {
    return t('CONVERSATION.LEGACY_QUEUE.RESCUE_LEVEL_1');
  }
  return '';
});

const followUpOwner = computed(
  () =>
    followUp.value.owner_name ||
    t('CONVERSATION.LEGACY_QUEUE.FOLLOW_UP_UNASSIGNED')
);
const followUpLabel = computed(() => {
  if (followUp.value.state === 'due') {
    return t('CONVERSATION.LEGACY_QUEUE.FOLLOW_UP_DUE_BADGE', {
      owner: followUpOwner.value,
    });
  }
  return t('CONVERSATION.LEGACY_QUEUE.FOLLOW_UP_BADGE', {
    owner: followUpOwner.value,
  });
});
</script>

<template>
  <div
    v-show="rescueLevel || isFollowUpActive || isAwaitingReply"
    class="flex min-w-0 flex-wrap gap-1"
  >
    <span
      v-if="isAwaitingReply"
      role="status"
      class="inline-flex w-fit flex-none items-center gap-1 whitespace-nowrap rounded-md bg-n-blue-3 px-1.5 py-0.5 text-xxs font-semibold leading-4 text-n-blue-11 outline outline-1 -outline-offset-1 outline-n-blue-6"
      :title="t('CONVERSATION.LEGACY_QUEUE.AWAITING_REPLY_DESCRIPTION')"
    >
      <span class="size-1.5 rounded-full bg-n-blue-9" />
      {{ t('CONVERSATION.LEGACY_QUEUE.AWAITING_REPLY') }}
    </span>
    <span
      v-if="rescueLevel"
      role="status"
      class="inline-flex w-fit flex-none items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xxs font-semibold leading-4"
      :class="
        rescueLevel === 2
          ? 'bg-n-ruby-3 text-n-ruby-11 outline outline-1 -outline-offset-1 outline-n-ruby-6'
          : 'bg-n-amber-3 text-n-amber-11 outline outline-1 -outline-offset-1 outline-n-amber-6'
      "
    >
      <span
        class="size-1.5 rounded-full"
        :class="rescueLevel === 2 ? 'bg-n-ruby-9' : 'bg-n-amber-9'"
      />
      {{ label }}
    </span>
    <span
      v-if="isFollowUpActive"
      role="status"
      class="inline-flex w-fit max-w-40 flex-none items-center gap-1 truncate whitespace-nowrap rounded-md bg-n-violet-3 px-1.5 py-0.5 text-xxs font-semibold leading-4 text-n-violet-11 outline outline-1 -outline-offset-1 outline-n-violet-6"
      :title="followUp.reason || followUpLabel"
    >
      {{ followUpLabel }}
    </span>
  </div>
</template>
